import { Link } from "react-router-dom";

export default function SnowballAvalanchePost() {
    return (
        <>
            <p>
                If you&apos;ve researched how to pay off debt, you&apos;ve hit the same
                two-way fork: <strong>avalanche</strong> (pay highest interest first)
                or <strong>snowball</strong> (pay smallest balance first). Spreadsheet
                people love avalanche. Behavioral economists love snowball. Here&apos;s
                how to pick the one that&apos;ll actually work for you.
            </p>

            <h2>The two methods, in one sentence each</h2>
            <ul>
                <li>
                    <strong>Avalanche:</strong> pay minimums on everything, throw every
                    extra dollar at the highest-APR debt. Repeat until debt-free.
                </li>
                <li>
                    <strong>Snowball:</strong> pay minimums on everything, throw every
                    extra dollar at the smallest-balance debt. As each debt is paid off,
                    roll that payment into the next-smallest balance (the &ldquo;snowball&rdquo;
                    effect).
                </li>
            </ul>

            <h2>Mathematically, avalanche wins — by a narrow margin</h2>
            <p>
                Avalanche minimizes total interest paid. On most real-world debt
                portfolios, it saves somewhere between 2% and 8% in total interest
                versus snowball. For most people that&apos;s $200–$1,500 across the
                life of their payoff plan.
            </p>
            <p>
                Not nothing. But not transformative either. Run your actual numbers in
                our <Link to="/calculator/debt-payoff">Debt Payoff Calculator</Link> —
                you&apos;ll usually find the interest difference is smaller than you
                expect.
            </p>

            <h2>Behaviorally, snowball wins — by a wide margin</h2>
            <p>
                Multiple academic studies (notably a 2012 Northwestern Kellogg paper by
                Gal &amp; McShane) have found that people who use the snowball method
                are <strong>significantly more likely to actually finish their debt
                payoff plan</strong>. The reason is simple: paying off a $400 medical
                bill in month one feels like winning. Paying off $400 of principal on a
                $14,000 credit card at 22% APR feels like nothing.
            </p>
            <p>
                Early wins compound psychologically. By the time you&apos;re 3–4 debts
                in, you&apos;re on a streak — and streaks finish plans.
            </p>

            <h2>The honest comparison: $15K debt in 3 accounts</h2>
            <ul>
                <li>Medical collection: $800 at 0% APR, $25/mo minimum</li>
                <li>Credit card: $4,200 at 24% APR, $85/mo minimum</li>
                <li>Auto loan: $10,000 at 6% APR, $250/mo minimum</li>
                <li>Extra payment budget: $400/mo</li>
            </ul>
            <p>
                <strong>Avalanche (CC → auto → medical):</strong> ~29 months to debt-free,
                ~$1,650 total interest.
            </p>
            <p>
                <strong>Snowball (medical → CC → auto):</strong> ~30 months to debt-free,
                ~$1,780 total interest.
            </p>
            <p>
                One extra month and $130 more interest. In exchange, you pay off a full
                debt by month 3 (the medical collection) — a huge psychological win
                that most avalanche plans don&apos;t deliver until month 12+.
            </p>

            <h2>How to choose, honestly</h2>
            <p>Ask yourself three questions:</p>
            <ol>
                <li>
                    <strong>Do you have at least one debt that&apos;s much smaller than
                    the others?</strong> If yes — snowball gives you a satisfying win
                    within the first 3–6 months. Worth the small interest premium.
                </li>
                <li>
                    <strong>Is one of your debts at a dramatically higher APR
                    (25%+)?</strong> If yes — avalanche shines. Credit cards north of
                    25% APR are bleeding you badly; the math punishment for ignoring
                    them is real.
                </li>
                <li>
                    <strong>Have you successfully stuck with a long financial plan
                    before?</strong> If yes — you can probably handle avalanche&apos;s
                    delayed gratification. If no — snowball is the right choice.
                    Seriously.
                </li>
            </ol>

            <h2>The hybrid: &ldquo;Avalanche with a morale win&rdquo;</h2>
            <p>
                My personal favorite. Start with snowball: knock out one or two small
                debts in the first few months to get the dopamine hit. Then switch to
                avalanche for the rest to minimize interest. You get the behavioral
                boost AND the math advantage.
            </p>

            <h2>What matters more than the method</h2>
            <p>
                The single biggest predictor of successful debt payoff isn&apos;t
                method — it&apos;s whether you stop accumulating new debt <em>while</em>
                paying off the old. Every additional credit card swipe during your
                payoff plan is math running backward. If you can&apos;t stop adding to
                the balance, neither snowball nor avalanche will save you.
            </p>
            <p>
                Practical tip: during active debt payoff, keep one low-limit credit
                card for emergencies and store the others literally out of reach (a
                ziploc in the freezer is the classic trick). Make spending friction
                your friend.
            </p>

            <h2>Related tools</h2>
            <ul>
                <li><Link to="/calculator/debt-payoff">Debt Payoff Calculator</Link> — run both strategies side by side</li>
                <li><Link to="/calculator/credit-card-payoff">Credit Card Payoff Calculator</Link> — model a single-card payoff</li>
                <li><Link to="/calculator/savings-goal">Savings Goal Calculator</Link> — build the post-debt emergency fund</li>
            </ul>
        </>
    );
}
